import { useRender } from "@base-ui/react/use-render"
import type * as React from "react"
import { mergeClassName } from "../internal/mergeClassName.js"
import { Display, Eyebrow, Heading, type HeadingLevel, Lede } from "../typography/Typography.js"
import "./Layout.css"

/**
 * Layout — the nine micro-primitives every page archetype is assembled from.
 * They ship in ONE file because each is three to ten lines of markup: splitting
 * them would leave nine files whose combined content is smaller than this
 * doc comment, and a reader looking for "how does this system space things"
 * would have to open all nine to find the one shared grammar.
 *
 * The grammar, in one place:
 *
 *  - SPACING is a closed union (`Space`) of the token scale's numeric steps
 *    plus its semantic roles, resolved to `var(--space-<value>)` in an inline
 *    style. Inline rather than a data-attr matrix because twelve values times
 *    four props is 48 CSS rules that say nothing a variable name does not.
 *  - GEOMETRY that is a closed choice (measure cap, column floor, depth) is a
 *    data-attribute, so the CSS stays static and greppable.
 *  - POLYMORPHISM is Base UI's `render` prop (never `as`, never `asChild`) and
 *    only where the primitive genuinely wraps semantic content — Stack and
 *    Cluster, which are frequently a `<ul>`, `<nav>` or `<footer>`.
 *  - SLOT MARKERS: every primitive sets `data-slot="<kebab-name>"`. Page
 *    archetypes select their children through THAT and nothing else, so a
 *    parent never reaches for a class name it does not own.
 *
 * Tokens only, no color of their own: these primitives draw at most a hairline
 * in `--color-border-default`. Nothing here reads an accent or a status hue.
 */

/**
 * A gap or padding step. Numeric entries are the 4px rhythm (there is no
 * `space-7`); the words are the semantic roles — prefer them when the gap has
 * a MEANING (`"cluster"` between badges, `"section"` between page bands),
 * because the role tracks a decision while a number tracks a pixel.
 */
export type Space = 1 | 2 | 3 | 4 | 5 | 6 | 8 | "field" | "cluster" | "card" | "form" | "section"

/** Cross-axis alignment, as the frozen wave-2 grammar spells it. */
export type Align = "start" | "center" | "end" | "stretch"

/** Main-axis distribution, as the frozen wave-2 grammar spells it. */
export type Justify = "start" | "center" | "end" | "stretch"

/** The single place a `Space` becomes CSS. */
function space(value: Space): string {
  return `var(--space-${value})`
}

/** The reading measure a Container caps at. */
export type ContainerWidth = "default" | "wide" | "console"

export interface ContainerProps extends React.ComponentProps<"div"> {
  /**
   * Which measure to cap at: `"default"` is the editorial reading column,
   * `"wide"` a roomier documentary page, `"console"` a dense operator surface
   * — NOT a reading column, so prose inside a console needs its own cap.
   * @default "default"
   */
  readonly width?: ContainerWidth
}

/**
 * Container — the centered measure. A cap rather than a fluid width: past
 * ~1600px a full-bleed text column stops being readable, and every archetype
 * in this family assumes SOMETHING has bounded the line length.
 */
export function Container({ width = "default", className, ...props }: ContainerProps) {
  return (
    <div
      className={mergeClassName("FfnContainer", className)}
      data-slot="container"
      data-width={width}
      {...props}
    />
  )
}

export interface SectionProps extends React.ComponentProps<"section"> {
  /**
   * Draw one hairline along the bottom edge. One rule per section, opt-in:
   * a page that rules every section has ruled nothing.
   * @default false
   */
  readonly divider?: boolean
}

/**
 * Section — the vertical rhythm unit. Its only geometry is
 * `--space-section` of padding above and below, which is why the token exists.
 */
export function Section({ divider = false, className, ...props }: SectionProps) {
  return (
    <section
      className={mergeClassName("FfnSection", className)}
      data-slot="section"
      data-divider={divider || undefined}
      {...props}
    />
  )
}

/** A PageHeader's title rank: the serif display face, or an h1-h6 step. */
export type PageHeaderLevel = "display" | HeadingLevel

export interface PageHeaderProps
  extends Omit<React.ComponentProps<"header">, "title" | "children"> {
  /**
   * Optional mono UPPERCASE kicker above the title. A kicker equal to the
   * title (trimmed, case-insensitive) is DROPPED rather than rendered, because
   * an eyebrow that echoes its heading adds a line and no information.
   */
  readonly eyebrow?: string
  /** The page title. */
  readonly title: string
  /** Optional intro paragraph, capped at a 75-character measure (WCAG 1.4.8). */
  readonly lede?: string
  /**
   * Title rank. `"display"` is the serif h1; a numeric level is used when the
   * header sits inside a larger outline and must not claim h1.
   * @default "display"
   */
  readonly level?: PageHeaderLevel
}

/**
 * PageHeader — the page masthead, composed from the typography roles rather
 * than restyling them. It is the page-level sibling of SectionKicker and
 * carries the same no-redundant-eyebrow rule.
 */
export function PageHeader({
  eyebrow,
  title,
  lede,
  level = "display",
  className,
  ...props
}: PageHeaderProps) {
  const showEyebrow =
    eyebrow !== undefined && eyebrow.trim().toLowerCase() !== title.trim().toLowerCase()

  return (
    <header
      className={mergeClassName("FfnPageHeader", className)}
      data-slot="page-header"
      {...props}
    >
      {showEyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {level === "display" ? <Display>{title}</Display> : <Heading level={level}>{title}</Heading>}
      {lede !== undefined && (
        <div className="FfnPageHeaderLede">
          <Lede>{lede}</Lede>
        </div>
      )}
    </header>
  )
}

export interface StackProps extends useRender.ComponentProps<"div"> {
  /** Gap between children. @default 4 */
  readonly gap?: Space
  /** Cross-axis (horizontal) alignment. @default "stretch" */
  readonly align?: Align
}

/**
 * Stack — vertical rhythm as a flex column with a gap. Never margins on the
 * children: a gap belongs to the container that chose it, so removing a child
 * cannot leave a stray margin behind.
 */
export function Stack({ gap = 4, align, render, style, ...props }: StackProps) {
  return useRender({
    render,
    defaultTagName: "div",
    props: {
      className: "FfnStack",
      "data-slot": "stack",
      "data-align": align,
      style: { gap: space(gap), ...style },
      ...props
    }
  })
}

export interface ClusterProps extends useRender.ComponentProps<"div"> {
  /** Gap between children. @default "cluster" */
  readonly gap?: Space
  /**
   * Cross-axis (vertical) alignment. `"baseline"` extends the shared grammar
   * for the one case only a row has: type of two sizes sitting on one line.
   * @default "center"
   */
  readonly align?: Align | "baseline"
  /**
   * Main-axis distribution. `"between"` extends the shared grammar for the
   * split-footer shape (label left, actions right) that is a row's whole job.
   * @default "start"
   */
  readonly justify?: Justify | "between"
  /**
   * Let children wrap to a second line. Wrapping is the default because a row
   * that cannot wrap overflows instead, and overflow is never the better bug.
   * @default true
   */
  readonly wrap?: boolean
}

/**
 * Cluster — a horizontal row of related controls, badges or metadata that
 * wraps rather than overflowing.
 */
export function Cluster({
  gap = "cluster",
  align,
  justify,
  wrap = true,
  render,
  style,
  ...props
}: ClusterProps) {
  return useRender({
    render,
    defaultTagName: "div",
    props: {
      className: "FfnCluster",
      "data-slot": "cluster",
      "data-align": align,
      "data-justify": justify,
      "data-wrap": wrap ? undefined : "false",
      style: { gap: space(gap), ...style },
      ...props
    }
  })
}

/** The narrowest a Grid column may get before the track count drops. */
export type GridMin = "sm" | "md" | "lg"

export interface GridProps extends React.ComponentProps<"div"> {
  /**
   * Column floor: 16rem / 18rem / 22rem. This, not a breakpoint, is what makes
   * the grid responsive — `auto-fit` drops a track whenever the floor no
   * longer fits, at every width, with no media query to keep in sync.
   * @default "md"
   */
  readonly min?: GridMin
  /** Gap between cells, both axes. @default 4 */
  readonly gap?: Space
}

/**
 * Grid — the intrinsically responsive card grid. The template wraps the floor
 * in `min(100%, …)` so a single card in a narrow viewport shrinks instead of
 * overflowing its parent.
 */
export function Grid({ min = "md", gap = 4, style, className, ...props }: GridProps) {
  return (
    <div
      className={mergeClassName("FfnGrid", className)}
      data-slot="grid"
      data-min={min}
      style={{ gap: space(gap), ...style }}
      {...props}
    />
  )
}

/** How many columns a ColGrid flows into once its container is wide enough. */
export type ColGridCols = 2 | 3 | 4

export interface ColGridProps extends React.ComponentProps<"div"> {
  /**
   * Column count at the widest container width. Narrower widths step down to
   * two, then one.
   * @default 3
   */
  readonly cols?: ColGridCols
  readonly children?: React.ReactNode
}

/**
 * ColGrid — newspaper column flow (CSS multi-column, not a grid), ruled with a
 * classic `column-rule` divider. Items never split across a column break.
 *
 * Two elements, deliberately: a container query cannot read the containment
 * context of the element that DECLARES it, so the outer div declares
 * `container: at-col-grid / inline-size` and the inner flow element is what
 * the queries size. Collapsing these into one element silently never matches.
 */
export function ColGrid({ cols = 3, children, ...props }: ColGridProps) {
  return (
    <div className="FfnColGrid" data-slot="col-grid" {...props}>
      <div className="FfnColGridFlow" data-cols={cols}>
        {children}
      </div>
    </div>
  )
}

/**
 * How far into a report a Band sits. The measure NARROWS as depth increases —
 * the page zooms in as the reader descends, so detail reads at a tighter line
 * length than the executive summary above it.
 */
export type BandDepth = "exec" | "mid" | "deep"

export interface BandProps extends React.ComponentProps<"section"> {
  /** Reading depth: 1180px / 940px / 720px measure. @default "mid" */
  readonly depth?: BandDepth
  /**
   * Draw one hairline along the bottom edge. Left off inside a ZoomReport,
   * which rules between its own bands and must not double the last one.
   * @default false
   */
  readonly divider?: boolean
  readonly children?: React.ReactNode
}

/**
 * Band — one horizontal register of a zooming report. It sets
 * `data-slot="band"` and `data-depth`, which is the entire contract a parent
 * report needs in order to rule hairlines between consecutive bands.
 */
export function Band({ depth = "mid", divider = false, children, ...props }: BandProps) {
  return (
    <section
      className="FfnBand"
      data-slot="band"
      data-depth={depth}
      data-divider={divider || undefined}
      {...props}
    >
      <div className="FfnBandMeasure">{children}</div>
    </section>
  )
}

export interface LeadProps extends React.ComponentProps<"section"> {}

/**
 * Lead — the above-the-fold band of a front page, ruled off from the columns
 * below it. One hairline is the whole design: a box here would compete with
 * the nameplate above and the column rules below.
 */
export function Lead(props: LeadProps) {
  return <section className="FfnLead" data-slot="lead" {...props} />
}
